<template>
  <teleport to="body">
    <transition name="file-preview-modal__visible">
      <div
        v-if="file"
        :class="['file-preview-modal__overlay', { 'full-screen': fullScreen }]"
        @mousedown="onCloseFromOverlay"
      >
        <div
          :class="[
            'file-preview-modal',
            {
              'full-screen': fullScreen,
            },
          ]"
          @click.stop="onClickDocument"
          @mousedown.stop
        >
          <TirPmButton
            class="file-preview-modal__close-button"
            :variant="TirPmButtonVariantEnum.TertiaryState"
            :size="
              fullScreen ? TirPmButtonSizeEnum.Big : TirPmButtonSizeEnum.Middle
            "
            :is-rounded="!fullScreen"
            @click="emit('close')"
          >
            <template #leftSlot>
              <CrossCompactIcon v-if="!fullScreen" />
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75ZM2.25 12C2.25 6.61522 6.61522 2.25 12 2.25C17.3848 2.25 21.75 6.61522 21.75 12C21.75 17.3848 17.3848 21.75 12 21.75C6.61522 21.75 2.25 17.3848 2.25 12ZM9.21967 9.21967C9.51256 8.92678 9.98744 8.92678 10.2803 9.21967L12 10.9393L13.7197 9.21967C14.0126 8.92678 14.4874 8.92678 14.7803 9.21967C15.0732 9.51256 15.0732 9.98744 14.7803 10.2803L13.0607 12L14.7803 13.7197C15.0732 14.0126 15.0732 14.4874 14.7803 14.7803C14.4874 15.0732 14.0126 15.0732 13.7197 14.7803L12 13.0607L10.2803 14.7803C9.98744 15.0732 9.51256 15.0732 9.21967 14.7803C8.92678 14.4874 8.92678 14.0126 9.21967 13.7197L10.9393 12L9.21967 10.2803C8.92678 9.98744 8.92678 9.51256 9.21967 9.21967Z"
                />
              </svg>
            </template>
          </TirPmButton>
          <div class="file-preview-modal__content">
            <div class="file-preview-modal__layer">
              <button
                class="file-preview-modal__slider-button left-button"
                v-if="isViewPrev && !fullScreen"
                @click="emit('onClickPrev')"
              >
                <ChevronLeftIcon
                  class="file-preview-modal__slider-button-icon"
                />
              </button>
              <button
                class="file-preview-modal__slider-button right-button"
                v-if="isViewNext && !fullScreen"
                @click="emit('onClickNext')"
              >
                <ChevronRightIcon
                  class="file-preview-modal__slider-button-icon"
                />
              </button>
              <div class="file-preview-modal__bottom">
                <div
                  class="file-preview-modal__bottom-image"
                  v-if="fileType === FileTypeEnum.Image"
                >
                  <span class="file-preview-modal__filename"
                    >{{ fileName }}.{{ fileExtension }}</span
                  >
                  <div class="file-preview-modal__image-controls">
                    <FilePreviewEditNamePopover
                      v-if="
                        fileInfo.Accesses?.includes(FilePreviewAccessEnum.Edit)
                      "
                      ref="$editNamePopover"
                      v-model:is-opened="isOpenedEditNamePopover"
                      :is-loading="
                        filesLoadingEditName.includes(fileInfo.FileId)
                      "
                      :file-name="fileInfo.Name"
                      @save-file-name="
                        emit('saveName', fileInfo.FileId, $event)
                      "
                    >
                      <TirPmButton
                        class="file-preview-modal__edit-button"
                        :size="TirPmButtonSizeEnum.Small"
                        :variant="TirPmButtonVariantEnum.PrimaryState"
                        @click.stop="onClickEdit"
                      >
                        <template #leftSlot>
                          <PencilSquareIcon />
                        </template>
                      </TirPmButton>
                    </FilePreviewEditNamePopover>

                    <TirPmButton
                      :size="TirPmButtonSizeEnum.Small"
                      :variant="TirPmButtonVariantEnum.PrimaryState"
                      @click="download"
                    >
                      <template #leftSlot>
                        <ArrowDownTrayIcon />
                      </template>
                    </TirPmButton>
                    <TirPmButton
                      :size="TirPmButtonSizeEnum.Small"
                      :variant="TirPmButtonVariantEnum.PrimaryState"
                      @click="emit('update:fullScreen', !fullScreen)"
                    >
                      <template #leftSlot>
                        <ArrowsPointingOutIcon v-if="!fullScreen" />
                        <ArrowsPointingInIcon v-else />
                      </template>
                    </TirPmButton>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="file-preview-modal__image-container"
              v-if="fileType === FileTypeEnum.Image"
            >
              <img
                class="file-preview-modal__image"
                :src="fileInfo.Url"
                :alt="fileInfo.Name"
              />
            </div>
            <TirPmVideoPlayer
              v-if="fileType === FileTypeEnum.Video"
              :video="videoInfo"
              @update-name="emit('saveName', fileInfo.FileId, $event)"
              @click.stop
            />
          </div>
          <div
            class="file-preview-modal__bottom-carousel"
            v-if="files.length > 1"
          >
            <div class="file-preview-modal__bottom-carousel-content">
              <button
                class="file-preview-modal__bottom-carousel-button left-button"
                v-if="isViewPrev && fullScreen"
                @click="emit('onClickPrev')"
              >
                <ChevronLeftIcon
                  class="file-preview-modal__bottom-carousel-icon"
                />
              </button>
              <button
                class="file-preview-modal__bottom-carousel-button right-button"
                v-if="isViewNext && fullScreen"
                @click="emit('onClickNext')"
              >
                <ChevronRightIcon
                  class="file-preview-modal__bottom-carousel-icon"
                />
              </button>
              <FilePreviewCarousel
                :files="files"
                :selected-file="fileInfo"
                @select-file="emit('update:file', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup generic="T extends IFilePreview">
import {
  computed,
  PropType,
  onUnmounted,
  defineEmits,
  onMounted,
  ref,
} from "vue";
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from "tir-components";
import {
  CrossCompactIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "tir-style-system/icons/outline";
import { IFilePreview } from "../../interfaces";
import { FileTypeEnum, FilePreviewAccessEnum } from "../../enums";
import { imageExtensions, videoExtensitons } from "../../constants";
import { downloadFile, getFileExtension } from "../../utils";
import { FilePreviewCarousel } from "../file-preview-carousel";
import { TirPmVideoPlayer, VideoModel } from "tir-pm-video-player";
import { FilePreviewEditNamePopover } from "../file-preview-edit-name-popover";

/**События */
const emit = defineEmits<{
  (e: "close"): void;
  /**Событие клика на кнопку скачать */
  (e: "onClickDownload"): void;
  /**Событие клика на кнопку вперед */
  (e: "onClickNext"): void;
  /**Событие клика на кнопку назад */
  (e: "onClickPrev"): void;
  /**Событие обновления выбранного файла*/
  (e: "update:file", value: IFilePreview): void;
  /**Событие обновления режима отображения*/
  (e: "update:fullScreen", value: boolean): void;
  /**Событие клика по кнопке редактирования */
  (e: "onClickEdit", fileId: T["FileId"]): void;
  /**Событие сохранения названия файла */
  (e: "saveName", fileId: T["FileId"], filename: string): void;
}>();

const props = defineProps({
  /**Файл */
  file: {
    type: Object as PropType<T>,
  },
  /**Список файлов */
  files: {
    type: Array as PropType<Array<IFilePreview>>,
    default: [],
  },
  /**Загрузка при редактировании названия */
  filesLoadingEditName: {
    type: Array as PropType<Array<T["FileId"]>>,
    default: [],
  },
  /**Отображать кнопку вперед */
  isViewNext: {
    type: Boolean,
    default: false,
  },
  /**Отображать кнопку назад */
  isViewPrev: {
    type: Boolean,
    default: false,
  },
  /**Полноэкранный режим */
  fullScreen: {
    type: Boolean,
    default: false,
  },
});

/**Ссылка на поповер редактирования названия файла */
const $editNamePopover = ref<InstanceType<typeof FilePreviewEditNamePopover>>();
/**Открыт ли поповер редактирования названия файла */
const isOpenedEditNamePopover = ref(false);
/**При клике на кнопку редактирования */
const onClickEdit = () => {
  // Если изображение
  if (imageExtensions.includes(fileExtension.value) && !['gif', 'svg'].includes(fileExtension.value)) {
    emit("close");
    emit("onClickEdit", fileInfo.value.FileId);
  } else {
    $editNamePopover.value?.open();
  }
};

const fileInfo = computed(() => props.file as T | undefined);

const onClickDocument = (e: any) => {
  if (!e?.target?.closest(".file-preview-modal__edit-button")) {
    document.body.click();
  }
};

/**Расщирение файла */
const fileExtension = computed(() => {
  return getFileExtension(fileInfo.value.Name);
});
/**Название файла (без расширения) */
const fileName = computed(() => {
  const lastDotIndex = fileInfo.value.Name.lastIndexOf(".");
  if (lastDotIndex === -1) return fileInfo.value.Name; // Если точки нет, возвращаем исходное имя
  return fileInfo.value.Name.substring(0, lastDotIndex);
});
/**Информация о видео */
const videoInfo = computed(() => {
  return new VideoModel({
    Link: fileInfo.value?.Url,
    Name: fileInfo.value?.Name,
    AutoPlay: true,
  });
});
/**Тип файла */
const fileType = computed(() => {
  switch (true) {
    case imageExtensions.includes(fileExtension.value):
      return FileTypeEnum.Image;
    case videoExtensitons.includes(fileExtension.value):
      return FileTypeEnum.Video;
    default:
      return FileTypeEnum.Other;
  }
});

/**При закрытии на оверлее */
const onCloseFromOverlay = () => {
  emit("close");
};

const download = () => {
  downloadFile(fileInfo.value.Url, `${fileInfo.value.Name}`);
};

const onKeyDown = (e: KeyboardEvent) => {
  if (!props.file) return;

  if (e.key === "Escape") {
    emit("close");
    return;
  }

  if (["ArrowLeft", "a"].includes(e.key)) {
    e.preventDefault();
    emit("onClickPrev");
    return;
  }
  if (["ArrowRight", "d"].includes(e.key)) {
    e.preventDefault();
    emit("onClickNext");
    return;
  }
};

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
});

onUnmounted(() => {
  if (props.file) {
    emit("close");
    document.body.style.removeProperty("overflow");
  }
  window.removeEventListener("keydown", onKeyDown);
});
</script>

<style lang="scss">
@import "./file-preview-modal.scss";
</style>
